import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  applyDocumentLang,
  createTranslator,
  readSavedLocale,
  resolveLocale,
  writeSavedLocale,
} from '@apm/i18n';
import type { Locale, Translator } from '@apm/i18n';
import { ko } from './ko.js';
import type { MessageKey } from './ko.js';
import { en } from './en.js';

/** `docs/22` §9. 콘솔(`bloom.console.locale`)과 오리진이 달라 충돌하지 않지만 접두사를 붙여 둔다. */
const STORAGE_KEY = 'bloom.locale';

const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { ko, en };

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: Translator<MessageKey>;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * **재생 트리보다 위에 둔다** (`main.tsx`). 트리 안에 두거나 언어로 `key` 를 갈면
 * `<video>` 가 다시 마운트되어 **영상이 처음으로 돌아간다.**
 *
 * **`location.reload()` 를 부르지 않는다** (절대 규칙 25 — 7·14 의 승계).
 * 언어는 상태이고, 상태가 바뀌면 문구만 다시 그려진다. `<video>` 와 hls.js 인스턴스는
 * 산다 — 그것이 이 Provider 가 재생 로직을 한 줄도 건드리지 않는 이유다.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const params = new URLSearchParams(window.location.search);
    const initial = resolveLocale({
      // `?lang=` 은 **저장하지 않는다** — 하네스와 데모 운영자가 잠시 고정해 보는 통로다.
      // `?channel=` 이 채널을 저장하지 않는 것과 같은 방침이다 (`docs/23` §7.3).
      query: params.get('lang'),
      saved: readSavedLocale(STORAGE_KEY),
      systemLanguages: window.navigator.languages,
    });
    applyDocumentLang(initial);
    return initial;
  });

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writeSavedLocale(STORAGE_KEY, next);
    applyDocumentLang(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t: createTranslator(DICTIONARIES[locale]) }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (value === null) throw new Error('LocaleProvider 밖에서 useLocale 을 불렀다.');
  return value;
}

/** 문구만 필요한 곳의 지름길. 대부분의 화면이 이것만 쓴다. */
export function useT(): Translator<MessageKey> {
  return useLocale().t;
}
